import { useEffect } from 'react';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import {
  OrganizationCustomFieldConfig,
  useGetOrganizationCustomFieldConfigsByOrganizationIdQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';

// Generous page size: active configs are bounded by the Weaviate pool (~60), but retired rows
// accumulate over time, so we still page through nextToken to be safe.
const PAGE_SIZE = 1000;

// The query's index has no sort key, so impose an order: oldest first, then field key.
const byCreation = (
  a: OrganizationCustomFieldConfig,
  b: OrganizationCustomFieldConfig
): number =>
  a.createdAt.localeCompare(b.createdAt) ||
  a.fieldKey.localeCompare(b.fieldKey);

/**
 * The active (non-retired) custom-field configs for the current organisation, across all pages,
 * in creation order. Drives the Internal Sourcing custom columns and their filter controls.
 */
export const useOrganizationCustomFieldConfigs = (): {
  customFieldConfigs: OrganizationCustomFieldConfig[];
  loading: boolean;
} => {
  const organization = useOrganization();

  const { data, loading, fetchMore } =
    useGetOrganizationCustomFieldConfigsByOrganizationIdQuery({
      variables: { organizationId: organization.id, limit: PAGE_SIZE },
      fetchPolicy: 'cache-first'
    });

  // Pull remaining pages if the backend returned a nextToken, merging items into the cache.
  const nextToken =
    data?.getOrganizationCustomFieldConfigsByOrganizationId?.nextToken;
  useEffect(() => {
    if (!nextToken) return;
    fetchMore({
      variables: { nextToken },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous;
        const nextPage =
          fetchMoreResult.getOrganizationCustomFieldConfigsByOrganizationId;
        const previousItems =
          previous.getOrganizationCustomFieldConfigsByOrganizationId?.items ??
          [];
        return {
          getOrganizationCustomFieldConfigsByOrganizationId: {
            ...nextPage,
            items: [...previousItems, ...(nextPage?.items ?? [])]
          }
        };
      }
    });
  }, [nextToken, fetchMore]);

  const customFieldConfigs = extractQueryListItems(data)
    .filter(
      (config): config is OrganizationCustomFieldConfig => !config.isDeleted
    )
    .sort(byCreation);

  return { customFieldConfigs, loading };
};
